/**
 * Reads a CSV, TSV or Excel (.xlsx) file in the browser, as the phone does (lib/services/orders/sheet_file.dart): every
 * cell as the text it shows (a time as "14:30", a date as "2026-10-30", a number in full, so a phone number Excel stored
 * as 8.035685313E9 keeps its digits). An .xlsx is a zip: it's opened with the browser's own DecompressionStream, and its
 * XML read by a small reader here, so nothing new is loaded.
 */

/** One row, numbered as the spreadsheet numbers it, so a problem can name the row someone will look for. */
export interface SheetRow {
  number: number;
  cells: string[];
  /** Hidden in the spreadsheet: filtered out, or hidden by hand. */
  hidden?: boolean;
}

export const cell = (r: SheetRow, column: number) => (column < r.cells.length ? r.cells[column] : '');
export const blank = (r: SheetRow) => r.cells.every((c) => !c.trim());

/** What's in a file: the rows of its first sheet that has any, as text. */
export interface SheetTable {
  rows: SheetRow[];
  sheetName?: string;
  /** Sheets of the same file that weren't read, by name. */
  otherSheets: string[];
}

export const columnsOf = (t: SheetTable) => t.rows.reduce((most, r) => Math.max(most, r.cells.length), 0);

export type SheetProblem = 'oldExcel' | 'notSpreadsheet' | 'empty' | 'tooBig' | 'oldBrowser';

export class SheetFileError extends Error {
  constructor(readonly problem: SheetProblem) {
    super(problem);
  }
}

/** A day's stops is far smaller than this. */
export const MAX_BYTES = 10 * 1024 * 1024;
/** The most any one part of an .xlsx may unpack to. */
const MAX_UNPACKED = 80 * 1024 * 1024;

const starts = (b: Uint8Array, magic: number[]) => b.length >= magic.length && magic.every((x, i) => b[i] === x);

/** The separator a file's name says it has: a .tsv's is a tab, however many commas its addresses hold. */
export const separatorOf = (name: string) => (/\.(tsv|tab)$/i.test(name) ? '\t' : undefined);

export async function readSheet(bytes: Uint8Array, sep?: string): Promise<SheetTable> {
  if (!bytes.length) throw new SheetFileError('empty');
  if (bytes.length > MAX_BYTES) throw new SheetFileError('tooBig');
  let table: SheetTable;
  if (starts(bytes, [0x50, 0x4b])) table = await xlsx(bytes);
  else if (starts(bytes, [0xd0, 0xcf, 0x11, 0xe0])) throw new SheetFileError('oldExcel');
  else table = csv(text(bytes), sep);
  if (table.rows.every(blank)) throw new SheetFileError('empty');
  return table;
}

// ── CSV ──────────────────────────────────────────────────────────────────

/** UTF-8 with or without its mark; Excel's "Unicode text" is UTF-16; an older Excel's CSV is in the Windows alphabet. */
function text(bytes: Uint8Array): string {
  if (starts(bytes, [0xef, 0xbb, 0xbf])) return new TextDecoder('utf-8').decode(bytes.subarray(3));
  if (starts(bytes, [0xff, 0xfe])) return new TextDecoder('utf-16le').decode(bytes.subarray(2));
  if (starts(bytes, [0xfe, 0xff])) return new TextDecoder('utf-16be').decode(bytes.subarray(2));
  if (bytes.subarray(0, 4096).includes(0)) throw new SheetFileError('notSpreadsheet');
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder('latin1').decode(bytes);
  }
}

/** [source] as comma, semicolon or tab separated rows, in quotes or not. */
export function csv(source: string, given?: string): SheetTable {
  let t = source.startsWith('﻿') ? source.slice(1) : source;
  if (/^\s*<(?:!doctype|html)/i.test(t)) throw new SheetFileError('notSpreadsheet');
  // Excel's own note of the separator: "sep=;".
  let sep: string | null = given ?? null;
  const said = /^sep=(.)\r?\n/.exec(t);
  if (said) {
    sep = said[1];
    t = t.slice(said[0].length);
  }
  sep ??= separator(t);
  const rows: SheetRow[] = [];
  let cells: string[] = [];
  let c = '';
  let quoted = false;
  let number = 1;
  const endCell = () => {
    cells.push(c.trim());
    c = '';
  };
  const endRow = () => {
    endCell();
    rows.push({ number: number++, cells });
    cells = [];
  };
  for (let i = 0; i < t.length; i++) {
    const ch = t[i];
    if (quoted) {
      if (ch === '"') {
        if (t[i + 1] === '"') {
          c += '"';
          i++;
        } else quoted = false;
      } else c += ch;
    } else if (ch === '"' && !c.trim()) {
      c = '';
      quoted = true;
    } else if (ch === sep) endCell();
    else if (ch === '\n') endRow();
    else if (ch === '\r') {
      if (t[i + 1] === '\n') i++;
      endRow();
    } else c += ch;
  }
  if (c || cells.length) endRow();
  return { rows, otherSheets: [] };
}

/** The separator of the first row with one: whichever comes most, outside quotes. */
function separator(t: string): string {
  for (const line of t.split(/\r?\n/).slice(0, 5)) {
    const bare = line.replace(/"[^"]*"/g, '');
    let best = ',';
    let most = 0;
    for (const s of [',', ';', '\t']) {
      const n = bare.split(s).length - 1;
      if (n > most) {
        most = n;
        best = s;
      }
    }
    if (most > 0) return best;
  }
  return ',';
}

// ── Zip ──────────────────────────────────────────────────────────────────

const u16 = (b: Uint8Array, at: number) => b[at] | (b[at + 1] << 8);
const u32 = (b: Uint8Array, at: number) => (b[at] | (b[at + 1] << 8) | (b[at + 2] << 16) | (b[at + 3] << 24)) >>> 0;

interface Entry {
  method: number;
  size: number;
  unpacked: number;
  offset: number;
}

/** The zip's files by name (its central directory). */
function entries(b: Uint8Array): Map<string, Entry> {
  let end = -1;
  for (let i = b.length - 22; i >= Math.max(0, b.length - 65557); i--) {
    if (u32(b, i) === 0x06054b50) {
      end = i;
      break;
    }
  }
  if (end < 0) throw new SheetFileError('notSpreadsheet');
  const count = u16(b, end + 10);
  let at = u32(b, end + 16);
  const out = new Map<string, Entry>();
  const names = new TextDecoder('utf-8');
  for (let k = 0; k < count; k++) {
    if (at + 46 > b.length || u32(b, at) !== 0x02014b50) throw new SheetFileError('notSpreadsheet');
    const n = u16(b, at + 28);
    const name = names.decode(b.subarray(at + 46, at + 46 + n));
    out.set(name, { method: u16(b, at + 10), size: u32(b, at + 20), unpacked: u32(b, at + 24), offset: u32(b, at + 42) });
    at += 46 + n + u16(b, at + 30) + u16(b, at + 32);
  }
  return out;
}

async function unpack(b: Uint8Array, e: Entry): Promise<Uint8Array> {
  if (e.unpacked > MAX_UNPACKED) throw new SheetFileError('tooBig');
  if (u32(b, e.offset) !== 0x04034b50) throw new SheetFileError('notSpreadsheet');
  const start = e.offset + 30 + u16(b, e.offset + 26) + u16(b, e.offset + 28);
  const data = b.slice(start, start + e.size);
  if (e.method === 0) return data;
  if (e.method !== 8) throw new SheetFileError('notSpreadsheet');
  // Safari before 16.4 and Firefox before 113 can't unpack an .xlsx: it's the browser, not the file.
  if (typeof DecompressionStream === 'undefined') throw new SheetFileError('oldBrowser');
  const stream = new Blob([data]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

// ── XML, as much as a workbook needs ─────────────────────────────────────

interface El {
  /** The local name: some writers prefix every element ("x:row"). */
  name: string;
  attrs: Record<string, string>;
  kids: (El | string)[];
  parent: El | null;
}

const ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" };
const unescape = (s: string) =>
  s.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (m, e: string) =>
    e[0] === '#' ? String.fromCodePoint(e[1].toLowerCase() === 'x' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10)) : (ENTITIES[e] ?? m),
  );
const local = (n: string) => n.slice(n.indexOf(':') + 1);

export function parseXml(s: string): El {
  const root: El = { name: '#root', attrs: {}, kids: [], parent: null };
  let cur = root;
  const re = /<!--[\s\S]*?-->|<\?[\s\S]*?\?>|<!\[CDATA\[([\s\S]*?)\]\]>|<![^>]*>|<(\/)?([^\s/>]+)((?:"[^"]*"|'[^']*'|[^>"'])*?)(\/)?>|([^<]+)/g;
  for (const m of s.matchAll(re)) {
    if (m[1] !== undefined) cur.kids.push(m[1]);
    else if (m[3]) {
      const name = local(m[3]);
      if (m[2]) {
        let e: El | null = cur;
        while (e && e !== root && e.name !== name) e = e.parent;
        if (e && e !== root) cur = e.parent!;
      } else {
        const attrs: Record<string, string> = {};
        for (const a of (m[4] ?? '').matchAll(/([^\s=]+)\s*=\s*(?:"([^"]*)"|'([^']*)')/g)) {
          const v = unescape(a[2] ?? a[3] ?? '');
          attrs[a[1]] = v;
          attrs[local(a[1])] ??= v;
        }
        const el: El = { name, attrs, kids: [], parent: cur };
        cur.kids.push(el);
        if (!m[5]) cur = el;
      }
    } else if (m[6] !== undefined) cur.kids.push(unescape(m[6]));
  }
  return root;
}

const kids = (e: El) => e.kids.filter((k): k is El => typeof k !== 'string');
function* named(e: El, name: string): Generator<El> {
  for (const k of kids(e)) {
    if (k.name === name) yield k;
    yield* named(k, name);
  }
}
const all = (e: El, name: string) => [...named(e, name)];
const textOf = (e: El): string => e.kids.map((k) => (typeof k === 'string' ? k : textOf(k))).join('');

/** The text of a string item: one `t`, or several runs, without the phonetic guides some Excel files carry. */
const runs = (item: El) =>
  all(item, 't')
    .filter((t) => t.parent?.name !== 'rPh')
    .map(textOf)
    .join('');

// ── Excel ────────────────────────────────────────────────────────────────

async function xlsx(bytes: Uint8Array): Promise<SheetTable> {
  let files: Map<string, Entry>;
  try {
    files = entries(bytes);
  } catch {
    throw new SheetFileError('notSpreadsheet');
  }
  try {
    const xml = async (path: string) => {
      const e = files.get(path);
      return e ? parseXml(new TextDecoder('utf-8').decode(await unpack(bytes, e))) : null;
    };
    const workbook = await xml('xl/workbook.xml');
    if (!workbook) throw new SheetFileError('notSpreadsheet');
    const targets = new Map<string, string>();
    const rels = await xml('xl/_rels/workbook.xml.rels');
    if (rels) for (const r of all(rels, 'Relationship')) if (r.attrs.Id && r.attrs.Target) targets.set(r.attrs.Id, r.attrs.Target);
    const sheets: { name: string; path: string }[] = [];
    for (const s of all(workbook, 'sheet')) {
      if (s.attrs.state === 'hidden' || s.attrs.state === 'veryHidden') continue;
      const target = targets.get(s.attrs.id ?? '');
      if (!target) continue;
      sheets.push({ name: s.attrs.name ?? '', path: target.startsWith('/') ? target.slice(1) : `xl/${target}` });
    }
    const base1904 = all(workbook, 'workbookPr').some((w) => ['1', 'true'].includes(w.attrs.date1904 ?? ''));
    const ss = await xml('xl/sharedStrings.xml');
    const strings = ss ? all(ss, 'si').map(runs) : [];
    const dates = dateStyles(await xml('xl/styles.xml'));
    for (let i = 0; i < sheets.length; i++) {
      const doc = await xml(sheets[i].path);
      if (!doc) continue;
      const rows = rowsOf(doc, strings, dates, base1904);
      if (rows.every(blank)) continue;
      const others: string[] = [];
      for (let j = i + 1; j < sheets.length; j++) {
        const d = await xml(sheets[j].path);
        if (d && all(d, 'c').length) others.push(sheets[j].name);
      }
      return { rows, sheetName: sheets[i].name, otherSheets: others };
    }
    throw new SheetFileError('empty');
  } catch (e) {
    if (e instanceof SheetFileError) throw e;
    throw new SheetFileError('notSpreadsheet');
  }
}

/** Which cell styles (by index) show a number as a date or a time. */
function dateStyles(doc: El | null): Set<number> {
  const out = new Set<number>();
  if (!doc) return out;
  const custom = new Set<number>();
  for (const f of all(doc, 'numFmt')) {
    const id = parseInt(f.attrs.numFmtId ?? '', 10);
    const code = (f.attrs.formatCode ?? '').replace(/"[^"]*"|\[[^\]]*\]|\\./g, '');
    if (!Number.isNaN(id) && /[dmyhs]/i.test(code)) custom.add(id);
  }
  const isDate = (id: number) => (id >= 14 && id <= 22) || (id >= 27 && id <= 36) || (id >= 45 && id <= 47) || (id >= 50 && id <= 58) || custom.has(id);
  const xfs = all(doc, 'cellXfs')[0];
  if (!xfs) return out;
  kids(xfs)
    .filter((x) => x.name === 'xf')
    .forEach((xf, i) => isDate(parseInt(xf.attrs.numFmtId ?? '0', 10) || 0) && out.add(i));
  return out;
}

function rowsOf(doc: El, strings: string[], dates: Set<number>, base1904: boolean): SheetRow[] {
  const out: SheetRow[] = [];
  let number = 0;
  for (const row of all(doc, 'row')) {
    number = parseInt(row.attrs.r ?? '', 10) || number + 1;
    const cells: string[] = [];
    for (const c of kids(row)) {
      if (c.name !== 'c') continue;
      const at = column(c.attrs.r) ?? cells.length;
      // A cell missing from the file is an empty one.
      while (cells.length < at) cells.push('');
      const v = cellText(c, strings, dates, base1904);
      if (at < cells.length) cells[at] = v;
      else cells.push(v);
    }
    out.push({ number, cells, hidden: ['1', 'true'].includes(row.attrs.hidden ?? '') });
  }
  return out;
}

/** "C5" → 2. */
function column(ref: string | undefined): number | null {
  const letters = ref ? /^[A-Za-z]+/.exec(ref)?.[0] : null;
  if (!letters) return null;
  let n = 0;
  for (const ch of letters.toUpperCase()) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
}

function cellText(c: El, strings: string[], dates: Set<number>, base1904: boolean): string {
  const type = c.attrs.t;
  if (type === 'inlineStr') {
    const inline = kids(c).find((k) => k.name === 'is');
    return inline ? runs(inline).trim() : '';
  }
  const ve = kids(c).find((k) => k.name === 'v');
  const v = ve ? textOf(ve).trim() : '';
  if (!v) return '';
  switch (type) {
    case 's': {
      const i = parseInt(v, 10);
      return i >= 0 && i < strings.length ? strings[i].trim() : '';
    }
    case 'str':
      return v;
    case 'd':
      return v.replace('T', ' ');
    case 'b':
      return v === '1' ? 'Yes' : 'No';
    case 'e':
      return '';
  }
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  const style = parseInt(c.attrs.s ?? '', 10);
  if (!Number.isNaN(style) && dates.has(style)) return dateText(n, base1904);
  return numberText(n, v);
}

/** A number as it reads: whole ones with no ".0" or exponent, so a phone number stored as 8.035685313E9 keeps its digits. */
export const numberText = (n: number, raw: string) => (Number.isInteger(n) && Math.abs(n) < 1e15 ? String(n) : raw);

/** A date or time cell: "14:30", "2026-10-30", or both. Excel's 1900 calendar counts from 30 December 1899. */
function dateText(serial: number, base1904: boolean): string {
  let days = Math.floor(serial);
  let minutes = Math.round((serial - days) * 24 * 60);
  if (minutes >= 24 * 60) {
    days++;
    minutes = 0;
  }
  const two = (n: number) => String(n).padStart(2, '0');
  const time = `${two(Math.floor(minutes / 60))}:${two(minutes % 60)}`;
  if (days === 0) return time;
  const d = new Date((base1904 ? Date.UTC(1904, 0, 1) : Date.UTC(1899, 11, 30)) + days * 864e5);
  const day = `${d.getUTCFullYear()}-${two(d.getUTCMonth() + 1)}-${two(d.getUTCDate())}`;
  return minutes === 0 ? day : `${day} ${time}`;
}
